import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useSub } from '@Services/User';
import { getProjects } from '@Services/Projects/Request';
import { useProjectList, setProjects } from '@Services/Projects';

import { ReactComponent as AddSVG } from '@Assets/Svgs/ic_add.svg';
import TX from '@Components/Common/Text';
import Button from '@Components/Common/Button';
import { LoadingLottie } from '@Components/Common/Lottie';

import ProjectItem from './ProjectItem';

const ProjectInfo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sub = useSelector(useSub);
  const projectList = useSelector(useProjectList);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const isNoProjectList = !(projectList && projectList.length);
  const setProjectData = useCallback(async () => {
    setIsDataFetching(true);

    const projects = await getProjects(sub);
    dispatch(setProjects({ projects }));

    setIsDataFetching(false);
  }, [dispatch, sub]);

  // eslint-disable-next-line consistent-return
  const handleNewProjectCreateClick = () => {
    if (projectList?.length >= 10) {
      return alert('프로젝트는 최대 10개까지 생성 가능합니다.');
    }
    navigate('/project_setting');
  };

  useEffect(() => {
    if (sub) {
      setProjectData();
    }
  }, [sub]);

  return (
    <>
      <UserProjectContainer>
        <ProjectText>내 프로젝트</ProjectText>
        <ButtonWrapper>
          <Button isTextOnly={false} onClick={handleNewProjectCreateClick}>
            <AddImage />새 프로젝트
          </Button>
        </ButtonWrapper>
      </UserProjectContainer>

      {isNoProjectList ? (
        <NoProjectListContainer>
          <NoProjectListText>작성 중인 프로젝트가 없어요.</NoProjectListText>
        </NoProjectListContainer>
      ) : (
        <>
          {isDataFetching ? (
            <LoadingLottieContainer>
              <LoadingLottie />
            </LoadingLottieContainer>
          ) : (
            <ProjectListContainer>
              {projectList.map(({ id, name, publishStatus, showing, projectSuspension, createdAtTimeStamp }) => (
                <ProjectItem
                  key={id}
                  projectId={id}
                  name={name}
                  publishStatus={publishStatus}
                  showing={showing}
                  projectSuspension={projectSuspension}
                  createdAtTimeStamp={createdAtTimeStamp}
                />
              ))}
            </ProjectListContainer>
          )}
        </>
      )}
    </>
  );
};

const UserProjectContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 32px;
`;

const ProjectText = styled(TX.Title1)`
  color: ${({ theme }) => theme.colors.ui_on_1};
`;

const AddImage = styled(AddSVG)`
  stroke: #fcfcfd;

  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

const NoProjectListContainer = styled.div`
  display: flex;
  justify-content: center;

  width: 100%;
  height: 114px;
  border-radius: 8px;
  margin-bottom: 48px;

  background-color: ${({ theme }) => theme.colors.ui_2};
`;

const NoProjectListText = styled(TX.FootnoteM)`
  color: ${({ theme }) => theme.colors.ui_on_4};
`;

const ProjectListContainer = styled.div`
  min-height: 70px;

  padding-bottom: 80px;
  overflow: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const LoadingLottieContainer = styled.div`
  height: 146px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 48px;
`;

const ButtonWrapper = styled.div`
  padding-bottom: 30px;
`;

export default ProjectInfo;
